import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Globe, Loader2, Check, AlertCircle, Code, LayoutGrid, Link2, ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * URL Import Modal — scrapes a website and converts it to page blocks.
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - () => void
 *   siteId        - string
 *   onBlocksReady - (blocks: Block[], message: string, siteSettings?: object) => void
 *   getAuthHeader - () => { Authorization: string }
 */
export default function URLImportModal({ isOpen, onClose, siteId, onBlocksReady, getAuthHeader }) {
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState('html'); // 'html' (faithful) is default for cloning
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');

  const isValidUrl = (u) => {
    try {
      const parsed = new URL(u.startsWith('http') ? u : `https://${u}`);
      return !!parsed.hostname && parsed.hostname.includes('.');
    } catch {
      return false;
    }
  };

  const handleImport = async () => {
    setError('');

    const trimmed = url.trim();
    if (!trimmed) {
      setError('Please paste a website URL');
      return;
    }
    if (!isValidUrl(trimmed)) {
      setError('Please enter a valid URL (e.g., https://example.com/page)');
      return;
    }

    const fullUrl = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;

    setIsLoading(true);
    setProgress('Fetching page content...');

    try {
      const headers = getAuthHeader();

      // Brief delay to show progress message before the long request
      setProgress('Scraping content, styles, and images...');

      const res = await axios.post(`${API}/sites/${siteId}/ai/import-url`, {
        url: fullUrl,
        mode,
      }, { headers, timeout: 120000 }); // 2 min timeout for scrape + AI

      const { blocks, message, page_title, site_settings, images_found, sections_found } = res.data;

      const summary = [
        `"${page_title}"`,
        `${blocks.length} block${blocks.length !== 1 ? 's' : ''}`,
        images_found ? `${images_found} images` : null,
        sections_found ? `${sections_found} sections detected` : null,
      ].filter(Boolean).join(' \u2022 ');

      toast.success(`Imported ${summary}`);
      onBlocksReady(blocks, message, site_settings);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Import failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setIsLoading(false);
      setProgress('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Import from URL
              </h2>
              <p className="text-[11px] text-stone-400">Clone any web page into your site builder</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-stone-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* URL Input */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Website URL</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <Link2 className="w-4 h-4 text-stone-400" />
              </div>
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !isLoading) handleImport(); }}
                placeholder="https://example.com/landing-page"
                disabled={isLoading}
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 placeholder:text-stone-300 disabled:opacity-50"
                autoFocus
              />
            </div>
            <p className="text-[10px] text-stone-400 mt-1">
              Paste any public web page URL. Works best with landing pages, marketing pages, and static content.
            </p>
          </div>

          {/* Mode toggle */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Import Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode('html')}
                disabled={isLoading}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                  mode === 'html'
                    ? 'border-stone-900 bg-stone-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <Code className={`w-4 h-4 flex-shrink-0 ${mode === 'html' ? 'text-stone-900' : 'text-stone-400'}`} />
                <div>
                  <div className="text-xs font-semibold text-stone-700">Faithful Clone</div>
                  <div className="text-[10px] text-stone-400">Preserves exact design & layout</div>
                </div>
              </button>
              <button
                onClick={() => setMode('blocks')}
                disabled={isLoading}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                  mode === 'blocks'
                    ? 'border-stone-900 bg-stone-50'
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <LayoutGrid className={`w-4 h-4 flex-shrink-0 ${mode === 'blocks' ? 'text-stone-900' : 'text-stone-400'}`} />
                <div>
                  <div className="text-xs font-semibold text-stone-700">Smart Blocks</div>
                  <div className="text-[10px] text-stone-400">Maps to editable block types</div>
                </div>
              </button>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <ExternalLink className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-[11px] text-blue-700 leading-relaxed">
                {mode === 'html' ? (
                  <>
                    <strong>Faithful Clone</strong> recreates the page's exact design using HTML/CSS blocks.
                    Colors, fonts, images, and layout are preserved. Each section becomes a separate editable block.
                  </>
                ) : (
                  <>
                    <strong>Smart Blocks</strong> converts content into native block types (hero, features, testimonials, etc.)
                    that are easier to edit. Some design details may differ from the original.
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Loading progress */}
          {isLoading && (
            <div className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-lg">
              <Loader2 className="w-4 h-4 text-stone-500 animate-spin flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-stone-700">{progress || 'Processing...'}</p>
                <p className="text-[10px] text-stone-400">This may take 30–60 seconds for complex pages</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-stone-100 bg-stone-50/50">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={isLoading || !url.trim()}
            className="text-xs bg-stone-900 hover:bg-stone-800 text-white gap-1.5"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5" />
                Import Page
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
